import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ArrowRightIcon, BurgerMenuIcon, LogoutIcon } from "components/icons";
import BUSelector from "../BUSelector";
import NavItem from "./NavItem";
import NavItemDropdown from "./NavItemDropdown";

import { useAuthStore, useBusinessUnitStore } from "@type/store";
import { useTranslationContext } from "context/TranslationContext";
import { generateNavigationItems } from "helpers/generateNavigationItems";
import { logout } from "helpers/logout";
import { useGetMainPathType } from "hooks/useGetMainPathType";
import cn from "lib/cn";

import { ENav, TNavItem } from "../types";

import navStyle from "./NavItem/NavItem.module.css";
import styles from "./Sidebar.module.css";

export interface SidebarProps {
  id?: string;
  collapsed: boolean;
  setCollapsed: Dispatch<SetStateAction<boolean>>;
}

const Sidebar = ({ collapsed, setCollapsed, id }: SidebarProps) => {
  const navigate = useNavigate();
  const mapBUList = useBusinessUnitStore((state) => state.mapBUList);

  const { t } = useTranslationContext();
  const [activeNav, setActiveNav] = useState<ENav>();
  const [openedChildren, setOpenedChildren] = useState<ENav | undefined>();
  const mainPathType = useGetMainPathType();
  const user = useAuthStore((state) => state.user);

  const handleClick = (href: string) => {
    navigate(href);
    setCollapsed(true);
  };

  useEffect(() => {
    setActiveNav(mainPathType);
  }, [mainPathType]);

  useEffect(() => {
    if (collapsed) {
      setOpenedChildren(undefined);
    }
  }, [collapsed]);

  const navItems: TNavItem[] = useMemo(() => {
    return generateNavigationItems({
      isSuperAdmin: user.isSuperAdmin,
      mapBusinessUnit: mapBUList,
    });
  }, [user, mapBUList]);

  return (
    <div
      data-testid={id}
      id={id}
      className="bg-secondary-500 text-white z-20 h-full text-base"
    >
      <div className="flex flex-col justify-between relative h-full">
        <div
          className={cn(
            "h-[4rem] flex  w-full items-center px-6 py-6",
            collapsed ? styles.wrapperLogo : "justify-end"
          )}
        >
          <div className="flex justify-center">
            <button onClick={() => setCollapsed(!collapsed)}>
              <BurgerMenuIcon
                width={25}
                height={25}
                className={collapsed ? undefined : styles.mirror}
              />
            </button>
          </div>
        </div>
        <nav className="flex flex-grow mb-2 flex-col justify-between bg-secondary-500">
          <BUSelector
            sidebarCollapsed={collapsed}
            setSidebarCollapsed={setCollapsed}
          />

          <ul className="flex flex-col space-y-1">
            {navItems.map((item, index) => {
              return (
                <li key={index} className={cn(["flex"])}>
                  {item.children?.length ? (
                    <NavItemDropdown
                      collapsed={collapsed}
                      activeNav={activeNav}
                      nav={item}
                      openedChildren={openedChildren}
                      setCollapsed={setCollapsed}
                      setOpenedChildren={setOpenedChildren}
                      onClick={handleClick}
                    />
                  ) : (
                    <NavItem
                      key={item.name}
                      collapsed={collapsed}
                      activeNav={activeNav}
                      nav={item}
                      setCollapsed={setCollapsed}
                    />
                  )}
                </li>
              );
            })}
          </ul>
          <div
            className={cn(
              "flex w-full h-full items-end  py-2",
              navStyle.navItemWrapper
            )}
          >
            <div className="w-full hover:bg-primary-800 px-4 py-4">
              <button
                className={cn([
                  "flex justify-between items-center w-full space-x-2",
                  "hover:bg-primary-800",
                ])}
                onClick={logout}
              >
                <div className="flex space-x-6">
                  <LogoutIcon width={20} height={20} />
                  <p className="text-base inline-flex whitespace-pre break-keep overflow-hidden">
                    {t("logout")}
                  </p>
                </div>
                <ArrowRightIcon width={17} height={17} />
              </button>
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default Sidebar;
